import { useNavigate, useParams } from "react-router-dom";
import { QrScanner } from "@yudiel/react-qr-scanner";
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogActions } from "../components/common/dialog";
import { Button } from "../components/common/button";
import { ScannerFinder } from "../components/qrScanner/scannerFinder";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { ModalHeader } from "../components/common/modalHeader";
import { useTranslation } from "react-i18next";
import { useFanInfo } from "../hooks/useFanInfo";
import { toast } from "react-toastify";

const audioUrl = new URL("../assets/beep.mp3", import.meta.url);

export function QrScannerPage() {
  const navigate = useNavigate();
  const { artistId, eventId, rewardId } = useParams();
  const artistIdNum = Number(artistId) || 0;
  const eventIdNum = Number(eventId) || 0;
  const rewardIdNum = Number(rewardId) || 0;
  const [ticketCode, setTicketCode] = useState<string>("");
  const qrResultRef = useRef<HTMLDialogElement>(null);
  const [enable, setEnable] = useState<boolean>(true);
  const { t } = useTranslation();
  const { isLoading, refetch } = useFanInfo(
    artistIdNum,
    eventIdNum,
    rewardIdNum,
    { ticketCode },
    false,
  );
  const audioRef = useRef(new Audio(audioUrl.href));

  useEffect(() => {
    if (ticketCode) {
      audioRef.current
        .play()
        .catch((e) => console.log("Error playing the sound", e));
      refetch().then((result) => {
        if (result.data) {
          setEnable(false);
          qrResultRef.current?.showModal();
        } else {
          toast.error(t("qrscanner_failure_msg"));
          setTicketCode("");
        }
      });
    }
    // eslint-disable-next-line
  }, [ticketCode, refetch]);

  return (
    <div className={"flex flex-col h-full overflow-hidden"}>
      <ModalHeader title={t("qrscanner_title")} />
      <QrScanner
        onResult={async (result) => {
          console.log(result.getText());
          if (!isLoading) {
            setTicketCode(result.getText());
          }
        }}
        onError={(error) => {
          if (
            error instanceof DOMException &&
            error.name === "NotAllowedError"
          ) {
            toast.error(t("qrscanner_nopermission"), {
              toastId: "qrscanner_nopermission",
            });
          } else {
            toast.error(error.name, { toastId: error.name });
          }
        }}
        scanDelay={1000}
        viewFinder={ScannerFinder}
        stopDecoding={!enable}
        hints={new Map().set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.QR_CODE,
        ])}
        audio={false}
        constraints={{
          facingMode: "environment",
          width: { min: 160, ideal: 320, max: 320 },
          height: { min: 160, ideal: 320, max: 320 },
        }}
      />

      <Dialog ref={qrResultRef}>
        <DialogActions className={"mt-0"}>
          <div className={"flex gap-3 flex-grow"}>
            <Button
              className={"btn-default basis-2/5 h-16"}
              onClick={() => {
                setTicketCode("");
                setEnable(true);
              }}
            >
              {t("qrscanner_retry")}
            </Button>
            <Button
              className={"btn-primary basis-3/5 h-16"}
              onClick={() => {
                navigate(
                  `/artists/${artistIdNum}/events/${eventIdNum}/rewards/${rewardIdNum}/user?ticketCode=${encodeURIComponent(ticketCode)}`,
                );
                setTicketCode("");
              }}
            >
              {t("qrscanner_result")}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
}
